import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../services/cloudApi';
import { getSocket, socketManager, ConnectionState } from '../services/socketService';
import { startPeriodicSync, resetSyncState, enqueueOperation, runSyncCycle } from '../services/syncManager';
import { useAdmin } from './AdminContext';
import { useConnectionMonitor } from './ConnectionMonitorContext';
import GoogleProfileConfirmModal from '../components/Common/GoogleProfileConfirmModal';
import i18n from '../i18n';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

const AuthContext = createContext(null);

const AUTH_STORAGE_KEY = 'notesplus_auth_user_v1';
const AUTH_TOKEN_STORAGE_KEY = 'notesplus_auth_token_v1';
const MIN_MANUAL_SYNC_INTERVAL = 2 * 1000;

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

// Rol etiketlerini döndür
const getRoleLabel = (role) => {
  const translated = i18n.t(`shared:authContext.roleLabels.${role}`, { defaultValue: '' });
  return translated || role || i18n.t('shared:authContext.roleLabels.free');
};

const translateAuthError = (code) => {
  if (!code) return i18n.t('errors:UNEXPECTED_ERROR');
  const translated = i18n.t(`errors:${code}`, { defaultValue: '' });
  if (translated) return translated;
  return i18n.t('errors:UNEXPECTED_ERROR_WITH_CODE', { code });
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [showGoogleConfirmModal, setShowGoogleConfirmModal] = useState(false);
  const [pendingGoogleData, setPendingGoogleData] = useState(null);
  const lastManualSyncRef = useRef(0);
  
  // Plus membership state
  const [isPlusUser, setIsPlusUser] = useState(false);
  const [plusExpiryDate, setPlusExpiryDate] = useState(null);
  
  // API REACHABILITY TRACKING — kept in sync with ConnectionMonitor (no sticky one-shot)
  const [isApiReachable, setIsApiReachable] = useState(null); // null = checking, true = reachable, false = unreachable
  const connectivityCheckInFlightRef = useRef(null);
  const loginRetryCountRef = useRef(0);
  const MAX_LOGIN_RETRIES = 1; // Only retry once, then give up
  const { isServerOnline } = useConnectionMonitor();
  
  // Logout sırasında bootstrap'ın tekrar user set etmesini engelle
  const isLoggingOutRef = useRef(false);
  
  // Bootstrap işleminin sadece 1 kere çalışmasını sağla
  const hasBootstrappedRef = useRef(false);
  
  // Bootstrap/login sırasında rol değişim bildirimi atılmasını engelle
  const isInitializingRef = useRef(true);
  
  // REF HOISTING FIX: Bu ref'ler persistUser'dan ÖNCE tanımlanmalı (persistUser içinde kullanılıyor)
  // refreshUser'ın son versiyonunu sakla (sonsuz döngüyü önlemek için)
  const refreshUserRef = useRef(null);
  // logout'un güncel versiyonunu sakla (401 event handler'ında kullanılır)
  const logoutRef = useRef(null);
  // refreshUser için throttle mekanizması
  const lastRefreshTimeRef = useRef(0);
  const isRefreshingRef = useRef(false);
  // Son bildirilen rol değişikliğini sakla (tekrar bildirim göndermemek için)
  const lastNotifiedRoleChangeRef = useRef(null);
  // Son bilinen rolü sakla (rol değişikliğini doğru algılamak için)
  const lastKnownRoleRef = useRef(null);
  
  // AdminContext'i optional olarak kullan (circular dependency'yi önlemek için)
  const adminContext = useAdmin();
  
  // Impersonate durumunda impersonate token'ı, değilse normal token'ı kullan
  const getActiveToken = useCallback(() => {
    if (adminContext?.getActiveToken) {
      return adminContext.getActiveToken();
    }
    return authToken;
  }, [adminContext, authToken]);

  // Mirror ConnectionMonitor so isApiReachable recovers when the server returns
  // (no second polling loop — monitor already pings /health)
  useEffect(() => {
    setIsApiReachable(isServerOnline);
  }, [isServerOnline]);

  // Explicit health probe (login retry / manual). Always performs a real check — never sticky.
  const checkApiConnectivity = useCallback(async () => {
    if (connectivityCheckInFlightRef.current) {
      return connectivityCheckInFlightRef.current;
    }

    const run = async () => {
      try {
        let apiBaseUrl = null;
        if (typeof window !== 'undefined' && window.api?.getApiBaseUrl) {
          try {
            apiBaseUrl = await window.api.getApiBaseUrl();
          } catch (error) {
            console.warn('⚠️ [Auth] Failed to get API URL from Electron:', error);
          }
        }

        if (!apiBaseUrl) {
          try {
            const { initializeApiUrl, getApiBaseUrlAsync } = await import('../config/apiConfig');
            await initializeApiUrl();
            apiBaseUrl = await getApiBaseUrlAsync();
          } catch (error) {
            console.warn('⚠️ [Auth] Failed to initialize API config:', error);
          }
        }

        if (!apiBaseUrl) {
          setIsApiReachable(false);
          console.error('❌ [AUTH] API URL not available');
          return false;
        }

        let healthCheckUrl = apiBaseUrl.replace(/\/api\/?$/, '');
        if (!healthCheckUrl.endsWith('/health')) {
          healthCheckUrl = `${healthCheckUrl}/health`;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        try {
          const response = await fetch(healthCheckUrl, {
            method: 'GET',
            signal: controller.signal,
            headers: { Accept: 'application/json' },
            mode: 'cors',
          });

          clearTimeout(timeoutId);

          if (response.status >= 200 && response.status < 300) {
            setIsApiReachable(true);
            return true;
          }
          if (response.status >= 500 && response.status < 600) {
            // HTTP response from gateway — host is reachable (may still be degraded)
            setIsApiReachable(true);
            console.warn('⚠️ [Auth] API server reachable but backend error (status:', response.status, ')');
            return true;
          }
          setIsApiReachable(true);
          return true;
        } catch (fetchError) {
          clearTimeout(timeoutId);

          const isNetworkError =
            fetchError.name === 'AbortError' ||
            fetchError.message?.includes('Failed to fetch') ||
            fetchError.message?.includes('ERR_NAME_NOT_RESOLVED') ||
            fetchError.message?.includes('ERR_CONNECTION_REFUSED') ||
            fetchError.message?.includes('NetworkError') ||
            fetchError.code === 'ENOTFOUND' ||
            fetchError.code === 'ECONNREFUSED';

          if (isNetworkError) {
            const isLocalhost = apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1');
            if (isLocalhost) {
              console.error('❌ [OFFLINE] Localhost server unreachable');
            } else {
              console.error('❌ [AUTH] API domain unreachable.');
            }
            setIsApiReachable(false);
            return false;
          }

          setIsApiReachable(false);
          console.error('❌ [AUTH] API connectivity check failed:', fetchError.message);
          return false;
        }
      } catch (error) {
        setIsApiReachable(false);
        console.error('❌ [AUTH] API connectivity check error:', error);
        return false;
      }
    };

    connectivityCheckInFlightRef.current = run().finally(() => {
      connectivityCheckInFlightRef.current = null;
    });
    return connectivityCheckInFlightRef.current;
  }, []);

  // Token bootstrap: localStorage'dan token yükle (sadece bootstrap sırasında)
  useEffect(() => {
    // Logout sırasında token yükleme
    if (isLoggingOutRef.current) {
      return;
    }
    
    try {
      const storedToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (storedToken) {
        setAuthToken(storedToken);
      }
    } catch (error) {
      console.error('Kimlik doğrulama tokenı okunamadı:', error);
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  }, []);


  // User objesini normalize et - rol karmaşasını temizle
  // NOT: Bu fonksiyon persistUser'dan önce tanımlanmalı (hoisting sorunu)
  const normalizeUserRecord = useCallback((userData, email) => {
    if (!userData) return null;
    
    // Rolü tutarlı bir şekilde belirle: önce role, sonra membership_type, sonra membershipType
    let role = userData.role || userData.membership_type || userData.membershipType || 'free';
    
    // "user" rolü geçersizdir, otomatik olarak "free" yap
    if (role === 'user') {
      role = 'free';
    }
    
    // Sadece geçerli roller: admin, free, plus
    const validRoles = ['admin', 'free', 'plus'];
    if (!validRoles.includes(role)) {
      role = 'free';
    }
    
    const membershipType = role; // Her zaman role ile aynı tut
    
    return {
      ...userData,
      email: email || userData.email,
      role: role,
      membershipType: membershipType,
      membership_type: membershipType,
      isAdmin: userData.isAdmin || userData.is_admin === 1 || role === 'admin',
      status: userData.status || 'active',
      avatarUrl: userData.avatarUrl || userData.avatar_url || null,
      avatar_url: userData.avatarUrl || userData.avatar_url || null,
      plusExpiryDate: userData.plusExpiryDate || userData.plus_expiry_date || null,
      onboardingCompleted:
        userData.onboardingCompleted === true ||
        userData.onboarding_completed === true ||
        userData.onboardingCompleted === 1 ||
        userData.onboarding_completed === 1,
      onboarding_completed:
        userData.onboardingCompleted === true ||
        userData.onboarding_completed === true ||
        userData.onboardingCompleted === 1 ||
        userData.onboarding_completed === 1,
    };
  }, []);

  const persistUser = useCallback((userData, skipRoleNotification = false) => {
    // Logout sırasında user set etme
    if (isLoggingOutRef.current) {
      return;
    }
    
    try {
      // User data'yı normalize et
      const normalizedUser = userData ? normalizeUserRecord(userData, userData.email) : null;
      
      if (normalizedUser) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        
        // User değiştiğinde rol referanslarını güncelle — backend /me already returns effective role.
        // Defensive: if plusExpiryDate is in the past, never treat as Plus (except admin).
        const role = normalizedUser.role || 'free';
        const expiryRaw = normalizedUser.plusExpiryDate || normalizedUser.plus_expiry_date;
        let isPlus = role === 'plus' || role === 'admin';
        if (isPlus && role !== 'admin' && expiryRaw) {
          const exp = new Date(expiryRaw);
          if (!Number.isNaN(exp.getTime()) && exp.getTime() <= Date.now()) {
            isPlus = false;
          }
        }
        setIsPlusUser(isPlus);
        if (isPlus && expiryRaw) {
          setPlusExpiryDate(new Date(expiryRaw));
        } else if (!isPlus) {
          setPlusExpiryDate(null);
        } else {
          setPlusExpiryDate(null); // lifetime / admin
        }
        
        const isInitializing = isInitializingRef.current || skipRoleNotification;
        if (isInitializing) {
          lastKnownRoleRef.current = role;
          lastNotifiedRoleChangeRef.current = null;
        } else {
          lastKnownRoleRef.current = role;
        }
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setUser(null);
        setIsPlusUser(false);
        setPlusExpiryDate(null);
        lastKnownRoleRef.current = null;
        lastNotifiedRoleChangeRef.current = null;
      }
    } catch (error) {
      console.error('Kullanıcı bilgisi kaydedilemedi:', error);
    }
  }, [normalizeUserRecord]);

  const persistToken = useCallback((token) => {
    try {
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        setAuthToken(null);
        return;
      }
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
      setAuthToken(token);
    } catch (error) {
      console.error('Kimlik doğrulama tokenı kaydedilemedi:', error);
    }
  }, []);



  // Bootstrap: App ilk açıldığında token kontrolü yap ve user bilgisini API'den çek
  useEffect(() => {
    // Bootstrap zaten yapıldıysa veya logout sırasındaysa, tekrar çalışma
    if (hasBootstrappedRef.current || isLoggingOutRef.current) {
      return;
    }

    // Fresh-launch OAuth kontrolü API erişilebilirlik beklemeden yapılır.
    // Notes+ protokol URL'i ile açıldıysa pendingOAuthData IPC'den gelir; hemen giriş yap.
    if (window.api && window.api.getPendingOAuth) {
      window.api.getPendingOAuth().then((pending) => {
        if (hasBootstrappedRef.current || isLoggingOutRef.current) return;
        if (pending && pending.token && pending.user) {
          console.log('🔐 Fresh-launch OAuth data found, auto-logging in');
          hasBootstrappedRef.current = true;
          isInitializingRef.current = true;
          try {
            const userRecord = normalizeUserRecord(pending.user, pending.user.email);
            persistToken(pending.token);
            persistUser(userRecord, true);
            try {
              window.dispatchEvent(new CustomEvent('notesplus-user-logged-in', { detail: { userId: userRecord.id } }));
            } catch (_) {}
          } catch (e) {
            console.error('❌ Fresh-launch OAuth processing error:', e);
          }
          setIsLoading(false);
          setTimeout(() => { isInitializingRef.current = false; }, 1000);
        }
      }).catch((e) => console.warn('⚠️ getPendingOAuth failed:', e));
    }

    // Wait for API connectivity check before bootstrapping
    if (isApiReachable === null) {
      return; // Still checking connectivity
    }

    hasBootstrappedRef.current = true;

    // Bootstrap sırasında initialization flag'ini set et (rol bildirimi atılmasın)
    isInitializingRef.current = true;

    const bootstrap = async () => {
      try {
        // Token kontrolü - token yoksa user set etme
        const storedToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
        if (!storedToken) {
          // Token yok - sadece login sayfası göster
          setAuthToken(null);
          setUser(null);
          setIsLoading(false);
          isInitializingRef.current = false;
          return;
        }
        
        // Token var - API'den user bilgisini çek
        setAuthToken(storedToken);
        
        try {
          const { request } = await import('../services/cloudApi');
          const serverResponse = await request('/me', {
            method: 'GET',
            token: storedToken,
          });
          
          let serverUser = null;
          if (serverResponse) {
            if (serverResponse.user) {
              serverUser = serverResponse.user;
            } else if (serverResponse.ok && serverResponse.user) {
              serverUser = serverResponse.user;
            } else if (serverResponse.id || serverResponse.email) {
              serverUser = serverResponse;
            }
          }
          
          if (serverUser) {
            const normalizedUser = normalizeUserRecord(serverUser, serverUser.email);
            if (normalizedUser) {
              persistUser(normalizedUser, true);
            } else {
              // Geçersiz user data
              localStorage.removeItem(AUTH_STORAGE_KEY);
              localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
              setUser(null);
              setAuthToken(null);
            }
          } else {
            // Token geçersiz - temizle
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
            setUser(null);
            setAuthToken(null);
          }
        } catch (error) {
          // Token geçersiz veya API hatası - temizle
          console.error('Token doğrulama hatası:', error);
          localStorage.removeItem(AUTH_STORAGE_KEY);
          localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
          setUser(null);
          setAuthToken(null);
        }
      } catch (error) {
        console.error('Bootstrap hatası:', error);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        setUser(null);
        setAuthToken(null);
      } finally {
        setIsLoading(false);
        isInitializingRef.current = false;
      }
    };
    
    bootstrap();
  }, [normalizeUserRecord, persistUser, isApiReachable]);

  const syncEventHandler = useCallback((arg1, arg2) => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
      return;
    }

    try {
      if (typeof arg1 === 'string') {
        window.dispatchEvent(
          new CustomEvent('notesplus-sync-remote', {
            detail: {
              entity: arg1,
              records: Array.isArray(arg2) ? arg2 : [],
            },
          })
        );
        return;
      }

      if (arg1 && typeof arg1 === 'object') {
        window.dispatchEvent(
          new CustomEvent('notesplus-sync-operation', {
            detail: {
              operation: arg1,
              result: arg2,
            },
          })
        );
      }
    } catch (error) {
      console.warn('Senkronizasyon olayı yayınlanamadı:', error);
    }
  }, []);

  const requestImmediateSync = useCallback(
    async ({ force = false, skipPull = true } = {}) => {
      const activeToken = getActiveToken();
      if (!activeToken) {
        return;
      }

      const now = Date.now();
      if (!force && now - lastManualSyncRef.current < MIN_MANUAL_SYNC_INTERVAL) {
        return;
      }

      lastManualSyncRef.current = now;

      try {
        // Kaydet butonlarından çağrıldığında sadece queue flush yap, pull yapma
        // Periyodik sync zaten pull yapacak
        const currentUserId = user?.id;
        await runSyncCycle({ token: activeToken, userId: currentUserId, onSyncTick: syncEventHandler, skipPull });
      } catch (error) {
        console.warn('Anlık senkronizasyon başarısız:', error);
      }
    },
    [getActiveToken, syncEventHandler]
  );

  // Periyodik kullanıcı bilgisi kontrolü (rol değişiklikleri için)
  // CRITICAL FIX: useRef ile interval yönetimi - sonsuz döngüyü önlemek için
  const userCheckIntervalRef = useRef(null);
  const lastCheckTimeRef = useRef(0);
  const isCheckingRef = useRef(false);
  
  useEffect(() => {
    if (!authToken || !user?.id) {
      // Cleanup: Eğer token veya user yoksa interval'ı temizle
      if (userCheckIntervalRef.current) {
        clearInterval(userCheckIntervalRef.current);
        userCheckIntervalRef.current = null;
      }
      return undefined;
    }

    // Eğer zaten bir interval varsa, yeni bir tane oluşturma (sonsuz döngüyü önle)
    if (userCheckIntervalRef.current) {
      return undefined;
    }

    const USER_CHECK_INTERVAL = 5 * 60 * 1000; // 5 dakikada bir kontrol et (rol değişiklikleri için yeterli)
    const MIN_CHECK_INTERVAL = 2 * 60 * 1000; // Minimum 2 dakika bekle (çok sık kontrolü engelle)

    const checkUserInfo = async () => {
      // Sayfa görünür değilse kontrol yapma
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }

      // Eğer zaten kontrol ediliyorsa, bekle
      if (isCheckingRef.current) {
        return;
      }

      const now = Date.now();
      // Eğer çok yakın zamanda kontrol edildiyse, bekle
      if (now - lastCheckTimeRef.current < MIN_CHECK_INTERVAL) {
        return;
      }
      
      isCheckingRef.current = true;
      lastCheckTimeRef.current = now;

      try {
        // Ref'ten en güncel refreshUser'ı al (silent mode ile - periyodik kontrol)
        if (refreshUserRef.current) {
          await refreshUserRef.current(true); // silent = true
        }
      } catch (error) {
        // Periyodik kontrol sırasında hataları tamamen sessizce yut
        // Hiçbir log, hiçbir hata mesajı
      } finally {
        isCheckingRef.current = false;
      }
    };

    // İlk kontrolü hemen yapma - sadece interval başlat (gereksiz istekleri önle)
    // checkUserInfo(); // İlk kontrolü kaldırdık - sadece periyodik kontrol yeterli

    // Periyodik kontrol (5 dakikada bir - rol değişiklikleri için yeterli)
    userCheckIntervalRef.current = setInterval(checkUserInfo, USER_CHECK_INTERVAL);

    // Online olduğunda kontrol et (ama sadece sayfa görünürse)
    let onlineHandler = null;
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      onlineHandler = () => {
        // Online olduğunda hemen kontrol etme, sadece flag set et
        // Periyodik kontrol zaten çalışacak
      };
      window.addEventListener('online', onlineHandler);
    }

    return () => {
      if (userCheckIntervalRef.current) {
        clearInterval(userCheckIntervalRef.current);
        userCheckIntervalRef.current = null;
      }
      if (onlineHandler && typeof window !== 'undefined') {
        window.removeEventListener('online', onlineHandler);
      }
      // Reset flags
      isCheckingRef.current = false;
      lastCheckTimeRef.current = 0;
    };
  }, [authToken, user?.id]); // refreshUser'ı dependency'den çıkardık, useRef kullanıyoruz

  // Socket üzerinden admin rol güncellemesini anlık yakala.
  // socketManager.subscribe ile her yeni bağlantıda dinleyiciyi yeniden kaydeder.
  // Önemli: refreshUser (API çağrısı) yerine payload'daki role ile direkt state güncelle.
  useEffect(() => {
    if (!authToken) return;

    const applyRole = (role) => {
      if (!role) return;
      setUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, role, membership_type: role, membershipType: role };
        try {
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });
      const isPlus = role === 'plus' || role === 'admin';
      setIsPlusUser(isPlus);
      if (!isPlus) setPlusExpiryDate(null);
    };

    const handler = ({ role } = {}) => {
      if (role) {
        // Socket payload'ından anlık güncelle — API çağrısı yok
        applyRole(role);
      } else {
        // Rol bilgisi yoksa API refresh yap
        lastRefreshTimeRef.current = 0;
        if (refreshUserRef.current) refreshUserRef.current(false, true);
      }
    };

    const tryRegister = () => {
      const s = getSocket();
      if (!s) return false;
      s.off('user:role-updated', handler);
      s.on('user:role-updated', handler);
      return true;
    };

    // Şu an bağlıysa hemen kaydet
    tryRegister();

    // Socket yeniden bağlandığında da kaydet (reconnect durumu)
    const unsub = socketManager.subscribe((state) => {
      if (state === ConnectionState.CONNECTED) tryRegister();
    });

    // Periyodik yeniden kayıt: socket race condition'a karşı (5 sn, 3 deneme)
    let retryCount = 0;
    const retryInterval = setInterval(() => {
      if (retryCount >= 3) { clearInterval(retryInterval); return; }
      if (tryRegister()) retryCount = 99; // başarılıysa dur
      else retryCount++;
    }, 5000);

    // Sayfa odağa gelince rol değişmiş olabilir — force refresh
    const onVisibilityChange = () => {
      if (!document.hidden) {
        lastRefreshTimeRef.current = 0;
        if (refreshUserRef.current) refreshUserRef.current(true, true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // window event: adminApi başarılı olduğunda hedef kullanıcıysa anında uygula
    const onRoleUpdated = (e) => {
      if (e?.detail?.role) applyRole(e.detail.role);
    };
    window.addEventListener('notesplus-role-updated', onRoleUpdated);

    return () => {
      unsub();
      clearInterval(retryInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('notesplus-role-updated', onRoleUpdated);
      const s = getSocket();
      if (s) s.off('user:role-updated', handler);
    };
  }, [authToken]);

  // Anlık üyelik/rol güncelleme talebi (herhangi bir bileşen 'notesplus-refresh-user' event'i fırlatabilir)
  useEffect(() => {
    if (!authToken) return;
    const handleForceRefresh = () => {
      if (refreshUserRef.current) {
        refreshUserRef.current();
      }
    };
    window.addEventListener('notesplus-refresh-user', handleForceRefresh);
    return () => window.removeEventListener('notesplus-refresh-user', handleForceRefresh);
  }, [authToken]);

  // 401 — oturum sona erdi: taslaklar Faz1 tarafından zaten kaydedilmiş,
  // kullanıcıya bildir ve çıkış yap (debounce: aynı anda birden fazla 401 gelirse tek sefer)
  useEffect(() => {
    if (!authToken) return;
    let handled = false;
    const handleUnauthorized = () => {
      if (handled) return;
      handled = true;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'warning',
            title: i18n.t('shared:authContextErrors.sessionExpiredTitle'),
            message: i18n.t('shared:authContextErrors.draftsSavedRelogin'),
          },
        }));
      }
      // Kısa gecikme: taslak kayıt işlemlerinin tamamlanmasını bekle
      setTimeout(() => {
        if (logoutRef.current) logoutRef.current({ silent: true });
      }, 300);
    };
    window.addEventListener('notesplus-unauthorized', handleUnauthorized);
    return () => window.removeEventListener('notesplus-unauthorized', handleUnauthorized);
  }, [authToken]);

  useEffect(() => {
    const activeToken = getActiveToken();
    // activeToken veya user yoksa sync'i durdur
    if (!activeToken || !user) {
      return undefined;
    }

    // Periyodik sync'i başlat (30 saniyede bir)
    const stop = startPeriodicSync({
      tokenRef: () => getActiveToken(),
      userIdRef: () => user?.id,
      onSyncTick: syncEventHandler,
    });

    // İlk girişte bir kez sync yap (hem push hem pull - yeni bilgisayarda verileri çekmek için)
    // CRITICAL: skipPull: false yaparak server'dan verileri çekiyoruz
    // Bu sayede yeni bir bilgisayarda login olduğunda tüm veriler server'dan çekilir
    runSyncCycle({ token: activeToken, userId: user?.id, onSyncTick: syncEventHandler, skipPull: false }).catch((error) => {
      console.warn('İlk senkronizasyon başarısız:', error);
    });

    return () => {
      stop?.();
    };
  }, [getActiveToken, user, syncEventHandler]);

  // isAuthenticated: Token ve user kontrolü
  const isAuthenticated = !!user && !!authToken;

  const login = useCallback(
    async ({ email, password }) => {
      try {
        const normalizedEmail = normalizeEmail(email);
        if (!normalizedEmail || !password) {
          return { success: false, error: i18n.t('shared:authContextErrors.emailPasswordRequired') };
        }

        // Check API connectivity first (but don't block if checking)
        if (isApiReachable === false) {
          // API is known to be unreachable - try one more time
          console.log('⚠️ [Auth] API was unreachable, retrying connectivity check...');
          const apiReachable = await checkApiConnectivity();
          if (!apiReachable) {
            return {
              success: false,
              error: i18n.t('shared:authContextErrors.serverUnreachableFull'),
              isOffline: true,
            };
          }
        }

        // If API reachability is unknown, check it now
        if (isApiReachable === null) {
          console.log('ℹ️ [Auth] API reachability unknown, checking...');
          const apiReachable = await checkApiConnectivity();
          if (!apiReachable) {
            return {
              success: false,
              error: i18n.t('shared:authContextErrors.serverUnreachableFull'),
              isOffline: true,
            };
          }
        }

        // Prevent infinite retry loops
        if (loginRetryCountRef.current >= MAX_LOGIN_RETRIES) {
          loginRetryCountRef.current = 0; // Reset after max retries
          return {
            success: false,
            error: i18n.t('shared:authContextErrors.loginAttemptFailedRetry'),
          };
        }

        let remoteResult = null;
        let remoteError = null;

        try {
          loginRetryCountRef.current += 1;
          remoteResult = await authApi.login({ email: normalizedEmail, password });
          loginRetryCountRef.current = 0; // Reset on success
        } catch (error) {
          remoteError = error;
          
          // Check for network/DNS errors
          const isNetworkError = 
            error.code === 'NETWORK_UNREACHABLE' ||
            error.message?.includes('Failed to fetch') ||
            error.message?.includes('ERR_NAME_NOT_RESOLVED') ||
            error.message?.includes('ERR_CONNECTION_REFUSED') ||
            (error.name === 'TypeError' && error.message?.includes('fetch'));
          
          if (isNetworkError) {
            // Mark API as unreachable
            setIsApiReachable(false);
            
            // Don't retry - API is unreachable
            loginRetryCountRef.current = 0;
            
            return {
              success: false,
              error: i18n.t('shared:authContextErrors.serverUnreachableCheckInternet'),
            };
          }
        }

        // ONLY mark as logged in if we got a successful response from server
        if (remoteResult?.success) {
          const userRecord = normalizeUserRecord(remoteResult.user, normalizedEmail);

          // Login sırasında initialization flag'ini set et (rol bildirimi atılmasın)
          isInitializingRef.current = true;
          
          persistToken(remoteResult.token);
          persistUser(userRecord, true); // skipRoleNotification = true
          
          // Context'lerin yeniden yüklemesi için event dispatch et
          try {
            window.dispatchEvent(new CustomEvent('notesplus-user-logged-in', { detail: { userId: userRecord.id } }));
          } catch (eventError) {
            console.warn('User login event yayınlanamadı:', eventError);
          }
          
          await requestImmediateSync({ force: true });
          
          // Login tamamlandıktan sonra initialization flag'ini kaldır
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 1000);
          
          // Giriş yaptıktan sonra hemen kullanıcı bilgilerini server'dan yenile (rol değişiklikleri için)
          setTimeout(async () => {
            try {
              if (refreshUserRef.current) {
                await refreshUserRef.current();
              }
            } catch (error) {
              // Sessizce geç
            }
          }, 500);
          
          return { success: true, user: userRecord };
        }

        // Handle auth errors (401, invalid credentials, etc.)
        if (remoteError && remoteError.status === 401) {
          persistToken(null);
          return {
            success: false,
            error: translateAuthError(remoteError.payload?.error || 'INVALID_CREDENTIALS'),
          };
        }

        if (remoteError && remoteError.code !== 'NETWORK_UNREACHABLE') {
          return {
            success: false,
            error: translateAuthError(remoteError.payload?.error) || remoteError.message || i18n.t('shared:authContextErrors.loginFailedGeneric'),
          };
        }

        // If we reach here, something unexpected happened
        return {
          success: false,
          error: i18n.t('shared:authContextErrors.loginFailedRetryLater'),
        };
      } catch (error) {
        // Catch any unexpected errors
        console.error('Login error:', error);
        return {
          success: false,
          error: getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.loginFailedFallback') }),
        };
      }
    },
    [
      isApiReachable,
      checkApiConnectivity,
      normalizeUserRecord,
      persistToken,
      persistUser,
      requestImmediateSync,
      translateAuthError,
    ]
  );

  const requestRegistrationCode = useCallback(
    async ({ email, name }) => {
      try {
        const normalizedEmail = normalizeEmail(email);
        if (!normalizedEmail) {
          return { success: false, error: i18n.t('errors:EMAIL_REQUIRED') };
        }

        const remoteResult = await authApi.requestRegisterCode({
          email: normalizedEmail,
          name,
        });

        return {
          success: true,
          expiresInSeconds: remoteResult?.expiresInSeconds,
          retryAfter: remoteResult?.retryAfter,
          expireLabel: remoteResult?.expireLabel,
        };
      } catch (error) {
        if (error?.code === 'LOCAL_SERVER_NOT_RUNNING') {
          return {
            success: false,
            error: getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.localServerNotRunning') }),
            errorCode: 'LOCAL_SERVER_NOT_RUNNING',
          };
        }

        if (error?.code === 'NETWORK_UNREACHABLE') {
          return {
            success: false,
            error: i18n.t('shared:authContextErrors.serverUnreachableCheckInternet'),
            errorCode: 'NETWORK_UNREACHABLE',
          };
        }

        const errorCode = error?.payload?.error || error?.code;
        return {
          success: false,
          error: translateAuthError(errorCode) || getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.codeSendFailedFallback') }),
          errorCode,
          retryAfter: error?.payload?.retryAfter,
        };
      }
    },
    []
  );

  const requestPasswordResetCode = useCallback(
    async ({ email }) => {
      try {
        const normalizedEmail = normalizeEmail(email);
        if (!normalizedEmail) {
          return { success: false, error: i18n.t('errors:EMAIL_REQUIRED') };
        }

        const remoteResult = await authApi.requestPasswordResetCode({
          email: normalizedEmail,
        });

        return {
          success: true,
          expiresInSeconds: remoteResult?.expiresInSeconds,
          retryAfter: remoteResult?.retryAfter,
          expireLabel: remoteResult?.expireLabel,
        };
      } catch (error) {
        if (error?.code === 'NETWORK_UNREACHABLE') {
          return {
            success: false,
            error: i18n.t('shared:authContextErrors.serverUnreachableCheckInternet'),
            errorCode: 'NETWORK_UNREACHABLE',
          };
        }

        const errorCode = error?.payload?.error || error?.code;
        return {
          success: false,
          error: translateAuthError(errorCode) || getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.codeSendFailedFallback') }),
          errorCode,
          retryAfter: error?.payload?.retryAfter,
        };
      }
    },
    []
  );

  const register = useCallback(
    async ({ email, password, name, code }) => {
      try {
        const normalizedEmail = normalizeEmail(email);
        if (!normalizedEmail || !password) {
          return { success: false, error: i18n.t('shared:authContextErrors.emailPasswordRequired') };
        }

        if (!code) {
          return { success: false, error: translateAuthError('CODE_REQUIRED') };
        }

        let remoteResult = null;
        let remoteError = null;

        try {
          remoteResult = await authApi.register({
            email: normalizedEmail,
            password,
            name,
            code,
          });
        } catch (error) {
          remoteError = error;
        }

        if (remoteResult?.success) {
          // Register sırasında initialization flag'ini set et (rol bildirimi atılmasın)
          isInitializingRef.current = true;
          
          const userRecord = normalizeUserRecord(remoteResult.user, normalizedEmail);
          persistToken(remoteResult.token);
          persistUser(userRecord, true); // skipRoleNotification = true
          // Context'lerin yeniden yüklemesi için event dispatch et
          try {
            window.dispatchEvent(new CustomEvent('notesplus-user-logged-in', { detail: { userId: userRecord.id } }));
          } catch (eventError) {
            console.warn('User login event yayınlanamadı:', eventError);
          }
          await requestImmediateSync({ force: true });
          
          // Register tamamlandıktan sonra initialization flag'ini kaldır
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 1000);
          
          // Kayıt olduktan sonra hemen kullanıcı bilgilerini server'dan yenile (rol değişiklikleri için)
          setTimeout(async () => {
            try {
              if (refreshUserRef.current) {
                await refreshUserRef.current();
              }
            } catch (error) {
              // Sessizce geç
            }
          }, 500);
          
          return { success: true, user: userRecord };
        }

        if (remoteError && (remoteError.status === 409 || remoteError.status === 422)) {
          return {
            success: false,
            error: translateAuthError(remoteError.payload?.error || 'EMAIL_ALREADY_EXISTS'),
            errorCode: remoteError.payload?.error,
            attemptsRemaining: remoteError.payload?.attemptsRemaining,
          };
        }

        if (remoteError && remoteError.code !== 'NETWORK_UNREACHABLE') {
          return {
            success: false,
            error: translateAuthError(remoteError.payload?.error) || remoteError.message || i18n.t('shared:authContextErrors.registrationFailedGeneric'),
          };
        }

        // Eğer buraya düştüysek ve remoteError yoksa, beklenmeyen bir durum vardır
        return {
          success: false,
          error: i18n.t('shared:authContextErrors.registrationFailedRetryLater'),
        };
      } catch (error) {
        console.error('Kayıt hatası:', error);
        return { success: false, error: error.message };
      }
    },
    [
      persistToken,
      persistUser,
      requestImmediateSync,
      normalizeUserRecord,
    ]
  );

  const resetPassword = useCallback(
    async ({ email, password, code }) => {
      try {
        const normalizedEmail = normalizeEmail(email);
        if (!normalizedEmail || !password) {
          return { success: false, error: i18n.t('shared:authContextErrors.emailPasswordRequired') };
        }

        if (!code) {
          return { success: false, error: translateAuthError('CODE_REQUIRED') };
        }

        let remoteResult = null;
        let remoteError = null;

        try {
          remoteResult = await authApi.resetPassword({
            email: normalizedEmail,
            password,
            code,
          });
        } catch (error) {
          remoteError = error;
        }

        if (remoteResult?.success) {
          // ResetPassword sırasında initialization flag'ini set et (rol bildirimi atılmasın)
          isInitializingRef.current = true;
          
          const userRecord = normalizeUserRecord(remoteResult.user, normalizedEmail);
          persistToken(remoteResult.token);
          persistUser(userRecord, true); // skipRoleNotification = true
          // Context'lerin yeniden yüklemesi için event dispatch et
          try {
            window.dispatchEvent(new CustomEvent('notesplus-user-logged-in', { detail: { userId: userRecord.id } }));
          } catch (eventError) {
            console.warn('User login event yayınlanamadı:', eventError);
          }
          await requestImmediateSync({ force: true });
          
          // ResetPassword tamamlandıktan sonra initialization flag'ini kaldır
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 1000);
          
          // Giriş yaptıktan sonra hemen kullanıcı bilgilerini server'dan yenile (rol değişiklikleri için)
          setTimeout(async () => {
            try {
              if (refreshUserRef.current) {
                await refreshUserRef.current();
              }
            } catch (error) {
              // Sessizce geç
            }
          }, 500);
          
          return { success: true, user: userRecord };
        }

        if (remoteError) {
          return {
            success: false,
            error: translateAuthError(remoteError.payload?.error) || remoteError.message || i18n.t('shared:authContextErrors.passwordResetFailedGeneric'),
            errorCode: remoteError.payload?.error || remoteError.code,
            attemptsRemaining: remoteError.payload?.attemptsRemaining,
          };
        }

        return {
          success: false,
          error: i18n.t('shared:authContextErrors.passwordResetFailedGeneric'),
        };
    } catch (error) {
      if (error?.code === 'NETWORK_UNREACHABLE') {
        return {
          success: false,
          error: i18n.t('shared:authContextErrors.serverUnreachableCheckInternet'),
          errorCode: 'NETWORK_UNREACHABLE',
        };
      }

      console.error('Şifre sıfırlama hatası:', error);
      return {
        success: false,
        error: translateAuthError(error?.payload?.error) || error.message,
        errorCode: error?.payload?.error || error?.code,
      };
      }
    },
    [
      persistToken,
      persistUser,
      requestImmediateSync,
      normalizeUserRecord,
    ]
  );

  // Logout: ATOMIK işlem - tüm auth state ve storage temizle
  const logout = useCallback(async (options = {}) => {
    const { silent = false, onComplete } = options;
    
    // Logout flag'ini set et (bootstrap useEffect'lerinin tekrar user set etmesini engelle)
    isLoggingOutRef.current = true;
    
    try {
      // refreshUser'ı durdur - logout sonrası user bilgisi çekilmemeli
      refreshUserRef.current = null;
      
      // ATOMIK TEMİZLİK: Tüm storage'ları ÖNCE temizle (state update'leri minimize et)
      // localStorage'dan TÜM auth verilerini ATOMIK olarak temizle
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      } catch (storageError) {
        // Storage hatası sessizce geç (silent mode)
        if (!silent) {
          console.error('localStorage temizlenirken hata:', storageError);
        }
      }
      
      // Sync state'i temizle (storage temizliğinden sonra)
      resetSyncState();
      
      // Rol referanslarını temizle
      lastKnownRoleRef.current = null;
      lastNotifiedRoleChangeRef.current = null;
      
      // State update'leri BATCH olarak yap (React render minimize et)
      if (!silent) {
        setUser(null);
        setAuthToken(null);
      }
      
      // FIX: window.dispatchEvent sadece silent=false iken çalışmalı
      // App kapanırken (silent=true) event dispatch etme - Electron kapanışını engelleyebilir
      if (!silent && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        // Logout event'i gönder (diğer context'lerin temizlenmesi için)
        try {
          window.dispatchEvent(new CustomEvent('notesplus-user-logged-out'));
          // Logout sonrası yönlendirme ve bildirim için event gönder
          window.dispatchEvent(new CustomEvent('notesplus-logout-complete', {
            detail: { shouldNavigate: true, shouldShowNotification: true }
          }));
        } catch (eventError) {
          // Event dispatch hatası sessizce geç
        }
      }
      
      // Callback çağır (navigate için) - sadece silent=false iken
      if (!silent && onComplete && typeof onComplete === 'function') {
        try {
          onComplete();
        } catch (callbackError) {
          // Callback hatası sessizce geç
        }
      }
    } catch (error) {
      // Genel hata - sessizce geç (silent mode)
      if (!silent) {
        console.error('Çıkış işlemi sırasında hata oluştu:', error);
      }
      
      // Hata olsa bile kritik temizlikleri yap
      refreshUserRef.current = null;
      lastKnownRoleRef.current = null;
      lastNotifiedRoleChangeRef.current = null;
      
      // Storage temizliği (hata durumunda da)
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      } catch (storageError) {
        // Sessizce geç
      }
      
      // State update sadece silent=false iken
      if (!silent) {
        setUser(null);
        setAuthToken(null);
      }
    }
    
    // Flag'i hemen sıfırla (yeni login için)
    isLoggingOutRef.current = false;
  }, []);

  const getCurrentUserId = useCallback(() => {
    return user?.id;
  }, [user]);

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      if (!authToken) {
        return { success: false, error: translateAuthError('UNAUTHORIZED') };
      }
      try {
        const { userApi } = await import('../services/cloudApi');
        await userApi.changePassword({
          token: authToken,
          currentPassword,
          newPassword,
        });
        return { success: true };
      } catch (error) {
        const code = error?.payload?.error || error?.payload?.code || error?.code;
        return {
          success: false,
          error: translateAuthError(code) || getApiErrorMessage(error, {
            fallback: i18n.t('shared:authContextErrors.passwordChangeFailed'),
          }),
        };
      }
    },
    [authToken]
  );

  const deleteAccount = useCallback(
    async (password) => {
      if (!authToken) {
        return { success: false, error: translateAuthError('UNAUTHORIZED') };
      }
      const userId = user?.id;
      try {
        const { userApi } = await import('../services/cloudApi');
        await userApi.deleteAccount({
          token: authToken,
          password: password || undefined,
        });

        // Clear offline note queue and related local caches for this user
        try {
          if (userId) {
            localStorage.removeItem(`notesplus_offline_notes_${userId}`);
            localStorage.removeItem(`notesplus_notes_order_user_${userId}`);
            localStorage.removeItem(`notesplus_notes_order_dirty_user_${userId}`);
          }
        } catch (_) {
          // ignore storage errors
        }

        await logout({ silent: false });
        return { success: true };
      } catch (error) {
        const code = error?.payload?.error || error?.payload?.code || error?.code;
        return {
          success: false,
          error: translateAuthError(code) || getApiErrorMessage(error, {
            fallback: i18n.t('shared:authContextErrors.accountDeletionFailed'),
          }),
        };
      }
    },
    [authToken, user?.id, logout]
  );

  const loginWithProvider = useCallback(
    async (provider) => {
      try {
        setIsLoading(true);

        let result;

        // Electron ortamında window.api.oauthLogin kullan
        if (window.api && window.api.oauthLogin) {
          // IPC invoke ile main process'ten yanıt bekle.
          // Fallback olarak oauth-login-complete IPC eventini de dinle:
          // second-instance ya da fresh-launch durumunda main process resolver'ı
          // bulamayıp doğrudan event gönderebilir; bu race her iki yolu da yakalar.
          result = await new Promise((resolve, reject) => {
            let settled = false;
            let removeListener = null;

            const settle = (data, isError) => {
              if (settled) return;
              settled = true;
              try { if (removeListener) removeListener(); } catch (_) {}
              if (isError) reject(data);
              else resolve(data);
            };

            // Fallback listener — main process zaten her callback'te
            // mainWindow.webContents.send('oauth-login-complete', ...) yapıyor.
            if (window.api.onOAuthLoginComplete) {
              removeListener = window.api.onOAuthLoginComplete((data) => {
                if (data && (data.token || data.isNewUser || data.error)) {
                  settle(data, false);
                }
              });
            }

            // Ana yol: IPC invoke (currentOAuthResolver çözülünce döner)
            window.api.oauthLogin(provider)
              .then((data) => settle(data, false))
              .catch((err) => settle(err, true));
          });
        } else {
          // Web ortamında normal OAuth flow
          // Parent window'un origin'ini gönder (callback sayfası doğru origin'e mesaj gönderebilsin)
          const parentOrigin = window.location.origin;
          const startResult = await authApi.getProviderStartUrl(provider, 'web', parentOrigin);
          if (!startResult.success) {
            return { success: false, error: i18n.t('shared:authContextErrors.oauthStartFailed') };
          }

          // Yeni pencere aç
          window.open(
            startResult.authUrl,
            'OAuth Login',
            'width=500,height=700'
          );

          // Callback beklemek için message listener
          result = await new Promise(async (resolve, reject) => {
            // API base URL'ini Electron'dan al (async)
            let apiOrigin = null;
            try {
              if (window.api?.getApiBaseUrl) {
                const apiBaseUrl = await window.api.getApiBaseUrl();
                if (apiBaseUrl) {
                  apiOrigin = new URL(apiBaseUrl).origin;
                }
              }
            } catch (error) {
              console.warn('⚠️ Failed to get API origin for OAuth:', error);
            }
            
            const allowedOrigins = [window.location.origin];
            if (apiOrigin) {
              allowedOrigins.push(apiOrigin);
            }

            let resolved = false;
            let timeout;

            const messageHandler = (event) => {
              // Origin kontrolü: window.location.origin veya API origin'i kabul et
              // Ayrıca, eğer mesaj OAuth callback'inden geliyorsa (type kontrolü ile) origin kontrolünü esnet
              const isValidOrigin = allowedOrigins.includes(event.origin) || 
                                    event.origin === 'null' || // file:// protokolü için
                                    (event.data && (event.data.type === 'oauth-success' || event.data.type === 'oauth-error' || event.data.type === 'oauth-new-user'));
              
              if (!isValidOrigin) {
                console.warn('OAuth callback: Geçersiz origin', event.origin, 'Beklenen:', allowedOrigins);
                return;
              }

              if (resolved) return; // Zaten işlem tamamlandı

              if (event.data.type === 'oauth-success') {
                resolved = true;
                window.removeEventListener('message', messageHandler);
                clearTimeout(timeout);
                // Popup closes itself from the API callback HTML.
                // Parent window.close() is blocked by Google's Cross-Origin-Opener-Policy.
                resolve(event.data);
              } else if (event.data.type === 'oauth-new-user') {
                resolved = true;
                window.removeEventListener('message', messageHandler);
                clearTimeout(timeout);
                resolve({
                  success: true,
                  requiresConfirmation: true,
                  isNewUser: true,
                  googleData: event.data.googleData,
                });
              } else if (event.data.type === 'oauth-error') {
                resolved = true;
                window.removeEventListener('message', messageHandler);
                clearTimeout(timeout);
                reject(new Error(event.data.error));
              }
            };

            window.addEventListener('message', messageHandler);

            // Timeout: 2 dakika sonra iptal et (eğer mesaj gelmezse)
            // NOT: window.closed kontrolü Cross-Origin-Opener-Policy nedeniyle çalışmıyor
            // Bu yüzden sadece timeout ve message event'ine güveniyoruz
            timeout = setTimeout(() => {
              if (resolved) return;
              resolved = true;
              window.removeEventListener('message', messageHandler);
              reject(new Error(i18n.t('shared:authContextErrors.oauthTimeout')));
            }, 2 * 60 * 1000);
          });
        }

        if (result.success) {
          // Yeni kullanıcı kontrolü
          if (result.isNewUser && result.googleData) {
            setPendingGoogleData(result.googleData);
            setShowGoogleConfirmModal(true);
            return { success: true, requiresConfirmation: true, googleData: result.googleData };
          }
          
          // Mevcut kullanıcı - normal login flow
          // LoginWithProvider sırasında initialization flag'ini set et (rol bildirimi atılmasın)
          isInitializingRef.current = true;
          
          const userRecord = normalizeUserRecord(result.user, result.user.email);

          persistToken(result.token);
          persistUser(userRecord, true); // skipRoleNotification = true
          // Context'lerin yeniden yüklemesi için event dispatch et
          try {
            window.dispatchEvent(new CustomEvent('notesplus-user-logged-in', { detail: { userId: userRecord.id } }));
          } catch (eventError) {
            console.warn('User login event yayınlanamadı:', eventError);
          }
          await requestImmediateSync({ force: true });
          
          // LoginWithProvider tamamlandıktan sonra initialization flag'ini kaldır
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 1000);
          
          // Giriş yaptıktan sonra hemen kullanıcı bilgilerini server'dan yenile (rol değişiklikleri için)
          setTimeout(async () => {
            try {
              if (refreshUserRef.current) {
                await refreshUserRef.current();
              }
            } catch (error) {
              // Sessizce geç
            }
          }, 500);
          
          return { success: true, user: userRecord };
        } else {
          return { success: false, error: result.error || i18n.t('shared:authContextErrors.oauthLoginFailedFallback') };
        }
      } catch (error) {
        console.error('Provider login error:', error);
        return { success: false, error: getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.oauthLoginFailedFallback') }) };
      } finally {
        setIsLoading(false);
      }
    },
    [
      persistToken,
      persistUser,
      requestImmediateSync,
      normalizeUserRecord,
    ]
  );

  const refreshUser = useCallback(async (silent = false, force = false) => {
    // Logout sonrası user null olabilir, kontrol et
    // Ayrıca refreshUserRef null ise (logout yapıldıysa), çalışma
    if (!user?.id || !user?.email || !authToken || !refreshUserRef.current) {
      return;
    }

    // Sayfa görünür değilse refresh yapma (force ile bypass edilebilir)
    if (!force && typeof document !== 'undefined' && document.hidden) {
      return;
    }

    // Throttle: Çok sık çağrıları engelle (minimum 10 saniye) (force ile bypass edilebilir)
    const REFRESH_THROTTLE_INTERVAL = 10 * 1000;
    const now = Date.now();
    if (!force && now - lastRefreshTimeRef.current < REFRESH_THROTTLE_INTERVAL) {
      return;
    }

    // Eğer zaten refresh ediliyorsa, bekle
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    lastRefreshTimeRef.current = now;
    
    try {
      // Server'dan kullanıcı bilgilerini çek
      try {
        const { request } = await import('../services/cloudApi');
        const serverResponse = await request('/me', {
          method: 'GET',
          token: authToken,
        });
        
        // Server yanıt formatını kontrol et
        let serverUser = null;
        if (serverResponse) {
          // Eğer direkt user objesi varsa
          if (serverResponse.user) {
            serverUser = serverResponse.user;
          }
          // Eğer ok ve user varsa
          else if (serverResponse.ok && serverResponse.user) {
            serverUser = serverResponse.user;
          }
          // Eğer direkt user bilgileri varsa (id, email, name, role vb.)
          else if (serverResponse.id || serverResponse.email) {
            serverUser = serverResponse;
          }
        }

        if (serverUser) {
          // CRITICAL: Merge serverUser with current user, prioritizing serverUser fields
          // This ensures role/membershipType from backend always overwrites local state
          const mergedUser = { ...user, ...serverUser };
          const updatedUser = normalizeUserRecord(mergedUser, user.email);
          
          // Rol değişikliği kontrolü - normalize edilmiş değerleri kullan
          const oldUserNormalized = normalizeUserRecord(user, user.email);
          const oldRole = oldUserNormalized.role || 'free';
          const newRole = updatedUser.role || 'free';
          
          // "user" → "free" geçişi bildirimi engelle (normalize işlemi, gerçek değişiklik değil)
          const isNormalizationChange = (oldRole === 'user' && newRole === 'free') || 
                                        (lastKnownRoleRef.current === 'user' && newRole === 'free');
          
          // Initialization sırasında bildirim atılmasın (bootstrap/login sırasında)
          const isInitializing = isInitializingRef.current;
          
          // Son bilinen rolü kontrol et (ilk kontrol ise kaydet)
          const isFirstCheck = lastKnownRoleRef.current === null;
          
          if (isFirstCheck || isInitializing) {
            // İlk kontrol veya initialization: Son bilinen rolü yeni rol ile set et (bildirim gösterilmesin)
            lastKnownRoleRef.current = newRole;
            // Initialization sırasında bildirim atılmasın
            if (isInitializing) {
              persistUser(updatedUser);
              return;
            }
          }
          
          // Gerçek rol değişikliği var mı kontrol et
          // (ilk kontrol değilse, initialization değilse ve normalize değişikliği değilse)
          const roleChanged = !isFirstCheck && 
                              !isInitializing &&
                              !isNormalizationChange && 
                              lastKnownRoleRef.current !== newRole;
          
          if (roleChanged) {
            // Aynı rol değişikliği için daha önce bildirim gönderilmiş mi kontrol et
            const roleChangeKey = `${lastKnownRoleRef.current}-${newRole}-${user.id}`;
            const alreadyNotified = lastNotifiedRoleChangeRef.current === roleChangeKey;
            
            if (!alreadyNotified) {
              // Son bildirilen rol değişikliğini kaydet
              const previousRole = lastKnownRoleRef.current;
              
              console.log('🔄 Kullanıcı rolü değişti:', {
                oldRole: previousRole,
                newRole,
                email: user.email
              });
              lastNotifiedRoleChangeRef.current = roleChangeKey;
              lastKnownRoleRef.current = newRole; // Son bilinen rolü güncelle
              
              // Rol değiştiğinde bir event fırlat (UI güncellemeleri için)
              if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                window.dispatchEvent(
                  new CustomEvent('notesplus-user-role-changed', {
                    detail: {
                      oldRole: previousRole,
                      newRole,
                      user: updatedUser,
                    },
                  })
                );
                
                // Toast bildirimi için de event fırlat (sadece bir kez)
                try {
                  const oldRoleLabel = getRoleLabel(previousRole);
                  const newRoleLabel = getRoleLabel(newRole);
                  window.dispatchEvent(
                    new CustomEvent('notesplus-show-toast', {
                      detail: {
                        type: 'success',
                        title: i18n.t('shared:authContext.membershipUpdatedTitle'),
                        message: i18n.t('shared:authContext.membershipUpdatedMessage', { oldRole: oldRoleLabel, newRole: newRoleLabel }),
                      },
                    })
                  );
                } catch (toastError) {
                  console.warn('Toast bildirimi gönderilirken hata:', toastError);
                }
              }
            } else {
              // Bildirim gönderilmiş ama rol hala değişmiş, sadece son bilinen rolü güncelle
              lastKnownRoleRef.current = newRole;
            }
          } else {
            // Rol değişmedi veya normalize değişikliği, son bilinen rolü güncelle
            lastKnownRoleRef.current = newRole;
          }
          
          persistUser(updatedUser);
          return;
        }
      } catch (serverError) {
        // Periyodik kontrol sırasında (silent mode) tüm hataları sessizce yut
        if (silent) {
          return; // Hiçbir hata mesajı gösterme, hiçbir log yapma
        }
        
        // 404 hatası - endpoint bulunamadı (server çalışmıyor olabilir)
        if (serverError.status === 404) {
          // 404 hatası, sessizce geç - server çalışmıyor veya endpoint yok
          return;
        }
        
        // Network hatalarını tamamen sessizce yut
        if (serverError.code === 'NETWORK_UNREACHABLE' || 
            serverError.code === 'LOCAL_SERVER_NOT_RUNNING' ||
            serverError.message?.includes('Sunucuya ulaşılamıyor') ||
            serverError.message?.includes('Local server çalışmıyor')) {
          // Network hatası, sessizce geç - hiçbir şey yapma
          return;
        }
        
        // 500 hatası veya INTERNAL_ERROR durumunda sessizce geç (server hatası)
        if (serverError.status === 500 || serverError.message === 'INTERNAL_ERROR') {
          // Server hatası, sessizce geç (çok sık log spam olmasın)
          if (Math.random() < 0.1) { // %10 ihtimalle log
            console.debug('Server hatası (500) - kullanıcı bilgisi alınamadı:', serverError.message);
          }
          return; // Fallback'e düşme, server hatası
        }
        
        // Diğer hatalar için sessizce geç
        // Sadece çok nadir durumlarda log
        if (Math.random() < 0.05) { // %5 ihtimalle log
          console.debug('Server\'dan kullanıcı bilgisi alınamadı:', serverError.message);
        }
      }
      
      // Fallback: Local database (sadece network hatası durumunda)
      if (window.api?.getUserByEmail && user?.email) {
        try {
          const dbUser = await window.api.getUserByEmail(user.email);
          if (dbUser) {
            const updatedUser = normalizeUserRecord({ ...user, ...dbUser }, user.email);
            persistUser(updatedUser);
          }
        } catch (dbError) {
          // Local database hatası sessizce yut
        }
      }
    } catch (error) {
      // Genel hatalar sessizce yut, çok sık log spam olmasın
      if (Math.random() < 0.1) { // %10 ihtimalle log
        console.debug('Kullanıcı bilgisi yenilenirken hata:', error);
      }
    } finally {
      // Refresh işlemi tamamlandı, flag'i sıfırla
      isRefreshingRef.current = false;
    }
  }, [user, persistUser, authToken, normalizeUserRecord]);

  // refreshUser tanımlandıktan sonra ref'i güncelle
  useEffect(() => {
    refreshUserRef.current = refreshUser;
  }, [refreshUser]);

  // logout tanımlandıktan sonra ref'i güncelle (401 handler'ında sonsuz döngü olmadan kullanmak için)
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Plus membership kontrolü
  const checkPlusStatus = useCallback(async () => {
    if (!authToken || !user?.id) {
      setIsPlusUser(false);
      setPlusExpiryDate(null);
      return;
    }

    try {
      const { licenseApi } = await import('../services/cloudApi');
      if (!licenseApi || !licenseApi.validate) {
        console.warn('⚠️ [AuthContext] licenseApi not available, skipping Plus status check');
        setIsPlusUser(false);
        setPlusExpiryDate(null);
        return;
      }
      const result = await licenseApi.validate({ token: authToken });

      if (result.success && result.isPlus) {
        setIsPlusUser(true);
        setPlusExpiryDate(result.expiryDate ? new Date(result.expiryDate) : null);

        // Plus bitimine yaklaşım uyarısı (7, 3, 1 gün kala — günde bir kez)
        if (result.expiryDate) {
          try {
            const expiryDateObj = new Date(result.expiryDate);
            const daysLeft = Math.ceil((expiryDateObj - new Date()) / (1000 * 60 * 60 * 24));
            if ([7, 3, 1].includes(daysLeft)) {
              const warnKey = `notesplus_plus_expiry_warned_${daysLeft}d_${expiryDateObj.toISOString().split('T')[0]}`;
              if (!localStorage.getItem(warnKey)) {
                localStorage.setItem(warnKey, '1');
                const msg = daysLeft === 1
                  ? i18n.t('shared:authContext.plusExpiryTomorrow')
                  : i18n.t('shared:authContext.plusExpiryDaysLeft', { days: daysLeft });
                window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                  detail: { type: 'plus', title: i18n.t('shared:authContext.plusExpiryTitle'), message: msg },
                }));
              }
            }
          } catch (_) {}
        }
      } else {
        setIsPlusUser(false);
        setPlusExpiryDate(null);
      }
    } catch (error) {
      console.error('Failed to check Plus status:', error);
      setIsPlusUser(false);
      setPlusExpiryDate(null);
    }
  }, [authToken, user?.id]);

  // Lisans anahtarını aktif et
  const activateLicenseKey = useCallback(async (licenseKey) => {
    if (!authToken || !user?.id) {
      return { success: false, error: 'NOT_AUTHENTICATED' };
    }
    
    let hwid = null;
    if (window.api?.getHWID) {
      try {
        const hwidResult = await window.api.getHWID();
        if (hwidResult.success) {
          hwid = hwidResult.hwid;
        } else {
          console.warn('Failed to get HWID:', hwidResult.error);
        }
      } catch (hwidError) {
        console.warn('HWID error:', hwidError);
      }
    }

    try {
      const { licenseApi } = await import('../services/cloudApi');
      const result = await licenseApi.activate({ token: authToken, licenseKey, hwid });

      if (result.success) {
        // Refresh user data to get updated plus_expiry_date
        await refreshUser(true);
        await checkPlusStatus(); // Re-check status immediately
        return { 
          success: true, 
          message: result.message || i18n.t('shared:authContextErrors.licenseActivatedSuccess'),
          expiryDate: result.expiryDate,
          isLifetime: result.isLifetime || false,
        };
      } else {
        return { success: false, error: result.error || i18n.t('shared:authContextErrors.licenseActivationFailedGeneric') };
      }
    } catch (error) {
      console.error('License activation error:', error);
      return { success: false, error: getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.licenseActivationFailedFallback') }) };
    }
  }, [authToken, user?.id, refreshUser, checkPlusStatus]);

  // Plus durumunu kontrol et (user veya authToken değiştiğinde)
  useEffect(() => {
    if (user?.id && authToken) {
      checkPlusStatus();
    } else {
      setIsPlusUser(false);
      setPlusExpiryDate(null);
    }
  }, [user?.id, authToken, checkPlusStatus]);

  // Yeni kullanıcı onay fonksiyonu
  const confirmNewUserFromGoogle = useCallback(
    async (googleData) => {
      try {
        setIsLoading(true);
        const { authApi } = await import('../services/cloudApi');
        const result = await authApi.confirmNewUserFromGoogle({ googleData });

        if (!result.success) {
          return { success: false, error: result.error || i18n.t('shared:authContextErrors.userCreationFailedFallback') };
        }

        // LoginWithProvider sırasında initialization flag'ini set et
        isInitializingRef.current = true;

        const userRecord = normalizeUserRecord(result.user, result.user.email);

        persistToken(result.token);
        persistUser(userRecord, true);
        await requestImmediateSync({ force: true });

        setShowGoogleConfirmModal(false);
        setPendingGoogleData(null);

        setTimeout(() => {
          isInitializingRef.current = false;
        }, 1000);

        setTimeout(async () => {
          try {
            if (refreshUserRef.current) {
              await refreshUserRef.current();
            }
          } catch (error) {
            // Sessizce geç
          }
        }, 500);

        return { success: true, user: userRecord };
      } catch (error) {
        console.error('Confirm new user error:', error);
        return { success: false, error: getApiErrorMessage(error, { fallback: i18n.t('shared:authContextErrors.userCreationFailedFallback') }) };
      } finally {
        setIsLoading(false);
      }
    },
    [
      persistToken,
      persistUser,
      requestImmediateSync,
      normalizeUserRecord,
    ]
  );

  const value = {
    user,
    isLoading,
    isAuthenticated, // Token ve user kontrolü
    isApiReachable, // null = checking, true = reachable, false = unreachable
    authToken,
    login,
    register,
    requestRegistrationCode,
    requestPasswordResetCode,
    resetPassword,
    logout,
    getCurrentUserId,
    changePassword,
    deleteAccount,
    loginWithProvider,
    confirmNewUserFromGoogle,
    showGoogleConfirmModal,
    pendingGoogleData,
    setShowGoogleConfirmModal,
    requestSync: requestImmediateSync,
    refreshUser,
    checkApiConnectivity, // Expose for manual re-check
    // Plus membership
    isPlusUser,
    plusExpiryDate,
    checkPlusStatus,
    activateLicenseKey,
    // Onboarding
    onboardingCompleted: user?.onboardingCompleted === true,
    markOnboardingComplete: async () => {
      if (!user || !authToken) {
        console.error('Onboarding complete skipped: missing user or token');
        return { success: false };
      }
      try {
        const { userApi } = await import('../services/cloudApi');
        const result = await userApi.completeOnboarding({ token: authToken });
        if (!result || result.ok === false || result.success === false) {
          console.error('Onboarding complete failed:', result);
          return { success: false };
        }
        const merged = result.user ? { ...user, ...result.user } : { ...user };
        persistUser({
          ...merged,
          onboardingCompleted: true,
          onboarding_completed: true,
        }, true);
        return { success: true };
      } catch (e) {
        console.error('Onboarding complete error:', e);
        return { success: false };
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Google Profile Confirm Modal */}
      {showGoogleConfirmModal && pendingGoogleData && (
        <GoogleProfileConfirmModal
          isOpen={showGoogleConfirmModal}
          googleData={pendingGoogleData}
          onConfirm={async (finalData) => {
            const result = await confirmNewUserFromGoogle(finalData);
            if (!result.success) {
              console.error('Failed to confirm new user:', result.error);
              // Hata durumunda modal'ı kapat
              setShowGoogleConfirmModal(false);
              setPendingGoogleData(null);
            }
          }}
          onCancel={() => {
            setShowGoogleConfirmModal(false);
            setPendingGoogleData(null);
          }}
        />
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;




